/**
 * Collings eSign API — talks to the native Django `esign` app (/api/esign/...).
 *
 * Authenticated calls (envelope management) go through the shared axios instance
 * (`api`), which carries the CRM's cookie/JWT auth + CSRF. The public signing
 * endpoints (`/sign/<token>/...`) require no auth — the token is the credential —
 * but reusing `api` is harmless.
 */
import api from './index';

const BASE = '/api/esign';

export const esignApi = {
  // ---- envelopes (authenticated) ----
  listEnvelopes: (params = {}) => api.get(`${BASE}/envelopes/`, { params }),
  agreements: (params = {}) => api.get(`${BASE}/envelopes/agreements/`, { params }),
  templates: () => api.get(`${BASE}/envelopes/templates/`),
  getEnvelope: (id) => api.get(`${BASE}/envelopes/${id}/`),

  createEnvelope: (formData) =>
    api.post(`${BASE}/envelopes/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateEnvelope: (id, data) => api.patch(`${BASE}/envelopes/${id}/`, data),
  deleteEnvelope: (id) => api.delete(`${BASE}/envelopes/${id}/`),

  saveFields: (id, payload) => api.put(`${BASE}/envelopes/${id}/signing-fields/`, payload),
  // send options: { inPerson } — when true the vendor signs on the agent's
  // device now (no email); the response carries `sign_token` for the handover.
  send: (id, { inPerson = false } = {}) =>
    api.post(`${BASE}/envelopes/${id}/send/`, inPerson ? { in_person: true } : {}),
  // RTA-style per-party signing launcher.
  // generate(): freeze + move to pending WITHOUT emailing anyone.
  generate: (id) => api.post(`${BASE}/envelopes/${id}/generate/`),
  // deliver(): launch ONE party. inPerson=true → returns { sign_token } for
  // on-device signing; otherwise emails just that signer. Identify by
  // recipientId (preferred) or signRequestId.
  deliver: (id, { recipientId, signRequestId, inPerson = false } = {}) =>
    api.post(`${BASE}/envelopes/${id}/deliver/`, {
      ...(recipientId != null ? { recipient_id: recipientId } : {}),
      ...(signRequestId != null ? { sign_request_id: signRequestId } : {}),
      in_person: !!inPerson,
    }),
  resend: (id) => api.post(`${BASE}/envelopes/${id}/resend/`),
  void: (id) => api.patch(`${BASE}/envelopes/${id}/void/`),
  saveAsTemplate: (id) => api.post(`${BASE}/envelopes/${id}/save-as-template/`),
  // Amend a COMPLETED authority IN PLACE (DOMA 13177543076): re-open it keeping
  // every existing signature, edit the form, then amendSend records the revision,
  // bakes the receipt and sends the other parties to re-initial just the change.
  amend: (id) => api.post(`${BASE}/envelopes/${id}/amend/`),
  amendSend: (id, signatureData) =>
    api.post(`${BASE}/envelopes/${id}/amend-send/`, { signature_data: signatureData }),
  amendCancel: (id) => api.post(`${BASE}/envelopes/${id}/amend-cancel/`),
  fromTemplate: (id) => api.post(`${BASE}/envelopes/${id}/from-template/`),

  // ---- templated authorities + AI contract analysis ----
  // List the fixed Collings sales-authority forms.
  listAuthorities: () => api.get(`${BASE}/envelopes/authorities/`),
  // Instantiate an authority into a draft envelope, auto-filled from the deal.
  createFromAuthority: (payload) =>
    api.post(`${BASE}/envelopes/authorities/create/`, payload),
  // Download the PRISTINE blank authority form (no envelope, no autofill) to
  // print / fill by hand. Returns a PDF blob.
  downloadAuthorityBlank: (key) =>
    api.get(`${BASE}/envelopes/authorities/${key}/blank/`, { responseType: 'blob' }),
  // AI field-detection on an envelope's PDF (e.g. an uploaded Contract of Sale).
  analyzeContract: (id) => api.post(`${BASE}/envelopes/${id}/analyze-contract/`),

  // ---- Contract of Sale (structured data entry; document generation TBD) ----
  // Create a draft COS envelope. Accepts a FormData (to carry the lawyer's
  // contract PDF as `file`, plus property_id/contact_id/title…) or a plain object.
  createContractOfSale: (payload) =>
    api.post(`${BASE}/envelopes/contract-of-sale/create/`, payload || {},
      (payload instanceof FormData)
        ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined),
  // Schema + current values + missing-required for the COS tabbed form.
  getCosForm: (id) => api.get(`${BASE}/envelopes/${id}/cos-form/`),
  // Save the agent's COS answers → form_state.
  saveCosForm: (id, values) =>
    api.patch(`${BASE}/envelopes/${id}/cos-form-values/`, { values }),
  // AI-fill the uploaded lawyer contract from the buyer sheet (+ vendor details
  // from the authority). Returns {layout:{fields,…}, filled, …} for the editor.
  cosAutofill: (id) => api.post(`${BASE}/envelopes/${id}/cos-autofill/`),
  // Attach/replace the lawyer's contract PDF on a COS envelope.
  cosSetContract: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`${BASE}/envelopes/${id}/cos-set-contract/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // ---- RTA-style templated-authority form ----
  // Schema + current values + missing-required for the tabbed data-entry form.
  getAuthorityForm: (id) => api.get(`${BASE}/envelopes/${id}/form/`),
  // Save the agent's answers → form_state + matching PDF fields.
  saveAuthorityForm: (id, values) =>
    api.patch(`${BASE}/envelopes/${id}/form-values/`, { values }),
  // Email the chosen conveyancer the "please prepare documents" notice.
  notifyConveyancer: (id) => api.post(`${BASE}/envelopes/${id}/notify-conveyancer/`),
  // OCR a driver-licence image → { vendor_name, vendor_address }.
  scanLicence: (id, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return api.post(`${BASE}/envelopes/${id}/scan-licence/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Agent applies / clears their own signature on the authority form page.
  agentSign: (id, signatureData) =>
    api.post(`${BASE}/envelopes/${id}/agent-sign/`, { signature_data: signatureData }),
  clearAgentSignature: (id) => api.delete(`${BASE}/envelopes/${id}/agent-sign/`),
  // Freshly-baked PDF (all current values + signatures) for the review-before-send step.
  previewBlob: (id) => api.get(`${BASE}/envelopes/${id}/preview/`, { responseType: 'blob' }),
  fileUrl: (id) => api.get(`${BASE}/envelopes/${id}/file-url/`),
  // Stream the envelope PDF bytes through the API (same-origin, HTTPS) so the editor /
  // detail viewer never fetches a raw cross-origin S3 URL — which the browser blocks on
  // the https prod page (mixed-content / S3 CORS → "Couldn't display the document.").
  fileBlob: (id) => api.get(`${BASE}/envelopes/${id}/file/`, { responseType: 'blob' }),
  auditLogs: (id) => api.get(`${BASE}/envelopes/${id}/audit-logs/`),

  // Supporting documents for a capture-only "file" question (e.g. the invoice
  // backing the marketing-expenses figure) — reference only, never baked into
  // the signed authority itself.
  listAttachments: (id, fieldKey) =>
    api.get(`${BASE}/envelopes/${id}/attachments/`, { params: { field_key: fieldKey } }),
  uploadAttachment: (id, fieldKey, file) => {
    const fd = new FormData();
    fd.append('field_key', fieldKey);
    fd.append('file', file);
    return api.post(`${BASE}/envelopes/${id}/attachments/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeAttachment: (id, attachmentId) =>
    api.delete(`${BASE}/envelopes/${id}/attachments/${attachmentId}/`),

  // Recipient autocomplete — all active CRM + portal users.
  recipientSuggestions: (q = '') => api.get(`${BASE}/recipient-suggestions/`, { params: { q } }),
  conveyancerSuggestions: (q = '') => api.get(`${BASE}/conveyancer-suggestions/`, { params: { q } }),

  addRecipient: (id, data) => api.post(`${BASE}/envelopes/${id}/recipients/`, data),
  updateRecipient: (id, recipientId, data) =>
    api.patch(`${BASE}/envelopes/${id}/recipients/${recipientId}/`, data),
  removeRecipient: (id, recipientId) =>
    api.delete(`${BASE}/envelopes/${id}/recipients/${recipientId}/`),

  // ---- public signing (token-based) ----
  getSigning: (token) => api.get(`${BASE}/sign/${token}/`),
  signingFileUrl: (token) => api.get(`${BASE}/sign/${token}/file-url/`),
  // Stream the PDF bytes through the API (same-origin, HTTPS) so the viewer never
  // fetches a raw cross-origin S3 URL — which the browser blocks on the https prod
  // page (mixed-content / S3 CORS → "Failed to load PDF file.").
  signingFileBlob: (token) =>
    api.get(`${BASE}/sign/${token}/file/`, { responseType: 'blob' }),
  // Baked, pre-filled PDF for the signer to download/print before signing (the
  // /file/ blob is the blank original; the values are only an on-screen overlay).
  signingPreviewBlob: (token) =>
    api.get(`${BASE}/sign/${token}/preview/`, { responseType: 'blob' }),
  signField: (token, fieldId, signatureData) =>
    api.post(`${BASE}/sign/${token}/sign/`, { field_id: fieldId, signature_data: signatureData }),
  setFieldValue: (token, fieldId, value) =>
    api.patch(`${BASE}/sign/${token}/field-value/`, { field_id: fieldId, value }),
  // Banking details / Body corporate / Legal representation - vendor self-entry
  // on their own signing page. Not applicable to the agent's own token (order 1).
  getCapturedDetails: (token) => api.get(`${BASE}/sign/${token}/captured-details/`),
  saveCapturedDetails: (token, values) =>
    api.patch(`${BASE}/sign/${token}/captured-details/`, { values }),
  complete: (token) => api.post(`${BASE}/sign/${token}/complete/`),
  approve: (token) => api.post(`${BASE}/sign/${token}/approve/`),
  decline: (token) => api.post(`${BASE}/sign/${token}/decline/`),

  // Triggered from the "Property Contract of Sale" screen's SEND OFFER LINK
  // TO BUYER button, but hits the buyer_portal app (not esign) - the invite
  // feeds the same Offer/Pipeline the Buyer Portal's own offer flow uses.
  sendOfferInvite: (propertyId, { name, email, message }) =>
    api.post(`/api/buyer-portal/properties/${propertyId}/offer-invites/`, { name, email, message }),
};

export default esignApi;
